import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import {
  FaPlus, FaEdit, FaTrash, FaCheck, FaSearch, FaUndo,
  FaSync, FaUpload, FaDownload, FaClone, FaRedo, FaTimes,
  FaCopy, FaFileExport, FaFilter, FaMinus, FaMagic, FaExchangeAlt,
  FaBan, FaEnvelopeOpen, FaEnvelope, FaShoppingCart, FaCreditCard,
  FaPlug, FaUnlink, FaKey, FaEye, FaCog, FaList
} from 'react-icons/fa';

/**
 * Action icon mappings - maps action types to their default icons
 */
const ACTION_ICONS = {
  add: FaPlus,
  edit: FaEdit,
  delete: FaTrash,
  save: FaCheck,
  search: FaSearch,
  reset: FaUndo,
  sync: FaSync,
  upload: FaUpload,
  download: FaDownload,
  clone: FaClone,
  refresh: FaRedo,
  close: FaTimes,
  cancel: FaTimes,
  copy: FaCopy,
  export: FaFileExport,
  filter: FaFilter,
  remove: FaMinus,
  generate: FaMagic,
  convert: FaExchangeAlt,
  invalidate: FaBan,
  markRead: FaEnvelopeOpen,
  markUnread: FaEnvelope,
  buy: FaShoppingCart,
  purchase: FaShoppingCart,
  checkout: FaCreditCard,
  connect: FaPlug,
  disconnect: FaUnlink,
  resetPassword: FaKey,
  view: FaEye,
  manage: FaCog,
  list: FaList,
};

/**
 * Default variant mappings - maps action types to their default color variants
 */
const ACTION_VARIANTS = {
  add: 'success',
  edit: 'primary',
  delete: 'danger',
  save: 'success',
  search: 'primary',
  reset: 'secondary',
  sync: 'primary',
  upload: 'primary',
  download: 'primary',
  clone: 'secondary',
  refresh: 'secondary',
  close: 'secondary',
  cancel: 'secondary',
  copy: 'secondary',
  export: 'primary',
  filter: 'secondary',
  remove: 'danger',
  generate: 'primary',
  convert: 'primary',
  invalidate: 'danger',
  markRead: 'secondary',
  markUnread: 'secondary',
  buy: 'success',
  purchase: 'success',
  checkout: 'success',
  connect: 'success',
  disconnect: 'warning',
  resetPassword: 'warning',
  view: 'secondary',
  manage: 'primary',
  list: 'secondary',
};

/**
 * Size class mappings
 */
const SIZE_CLASSES = {
  sm: { padding: 'p-1.5', icon: 'text-sm', text: 'text-xs' },
  md: { padding: 'p-2', icon: 'text-base', text: 'text-sm' },
  lg: { padding: 'p-2.5', icon: 'text-lg', text: 'text-base' },
};

/**
 * A unified action button component that supports multiple action types with consistent styling.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.action - Action type (add, edit, delete, save, etc.)
 * @param {ReactElement|ComponentType} [props.icon] - Optional custom icon (overrides default for action)
 * @param {Function} props.onClick - Click handler function
 * @param {string} props.title - Button title/aria-label (required for accessibility)
 * @param {boolean} [props.showText=false] - Whether to show text alongside icon
 * @param {string} [props.size='md'] - Button size (sm, md, lg)
 * @param {string} [props.variant] - Color variant (overrides default for action)
 * @param {boolean} [props.disabled=false] - Whether button is disabled
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {string} [props.type='button'] - Button type (button, submit, reset)
 * @returns {JSX.Element} The rendered ActionButton component
 * 
 * @example
 * <ActionButton
 *   action="add"
 *   onClick={handleAdd}
 *   title="Add new item"
 *   showText={true}
 * />
 * 
 * @example
 * <ActionButton
 *   action="delete"
 *   onClick={handleDelete}
 *   title="Delete item"
 *   variant="danger"
 * />
 */
const ActionButton = ({
  action,
  icon: customIcon,
  onClick,
  title,
  showText = false,
  size = 'md',
  variant: customVariant,
  disabled = false,
  loading = false,
  className = '',
  type = 'button',
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();

  // Validate action type
  if (!action || action === 'custom' && !customIcon) {
    console.warn(`ActionButton: action "${action}" requires a custom icon prop`);
  }

  // Determine icon to use
  let IconComponent = null;
  if (customIcon) {
    // Custom icon provided - can be React element or component
    if (React.isValidElement(customIcon)) {
      IconComponent = () => customIcon;
    } else if (typeof customIcon === 'function' || (typeof customIcon === 'object' && customIcon !== null && 'render' in customIcon)) {
      IconComponent = customIcon;
    }
  } else if (action && action !== 'custom' && ACTION_ICONS[action]) {
    // Use default icon for action
    IconComponent = ACTION_ICONS[action];
  }

  // Determine variant to use
  // For custom actions without explicit variant, default to primary (more visible than secondary)
  const variant = customVariant || (action && action !== 'custom' ? ACTION_VARIANTS[action] : 'primary') || 'primary';

  // Build variant classes using semantic colors
  let variantClasses = '';
  switch (variant) {
    case 'primary':
      variantClasses = semantic.actionButtonPrimary || 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500';
      break;
    case 'secondary':
      variantClasses = semantic.actionButtonSecondary || 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 dark:hover:bg-neutral-700 focus:ring-neutral-500';
      break;
    case 'tertiary':
      variantClasses = semantic.actionButtonTertiary || 'bg-transparent text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 focus:ring-neutral-400';
      break;
    case 'danger':
      variantClasses = semantic.actionButtonDanger || 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
      break;
    case 'success':
      variantClasses = semantic.actionButtonSuccess || 'bg-accent-600 text-white hover:bg-accent-700 focus:ring-accent-500';
      break;
    case 'warning':
      variantClasses = semantic.actionButtonWarning || 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500';
      break;
    default:
      variantClasses = semantic.actionButtonPrimary || 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500';
  }

  // Get size classes
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  // Determine if button is icon-only (no text)
  const isIconOnly = !showText;

  // Base classes - use rounded-full for icon-only buttons, rounded-md for buttons with text
  // For icon-only buttons, use aspect-square to ensure width matches height (perfect circle)
  // Use h-auto to let content determine height, then aspect-square makes width match
  const shapeClass = isIconOnly ? 'rounded-full aspect-square h-auto' : 'rounded-md';
  const baseClasses = `${sizeClasses.padding} ${shapeClass} transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center`;
  const isButtonDisabled = disabled || loading;

  // Build final className
  const finalClassName = `${baseClasses} ${variantClasses} ${className}`.trim();

  // Render icon
  const iconElement = IconComponent ? (
    <IconComponent className={sizeClasses.icon} />
  ) : null;

  // For icon-only buttons, use a ref to set min-width to match height
  const buttonRef = useRef(null);

  useEffect(() => {
    if (isIconOnly && buttonRef.current) {
      const buttonElement = buttonRef.current; // Capture ref value to avoid stale closure

      const updateMinWidth = () => {
        // Check if element still exists and is connected to DOM
        if (!buttonElement || !buttonElement.isConnected) {
          return;
        }
        const height = buttonElement.offsetHeight;
        if (height > 0) {
          buttonElement.style.minWidth = `${height}px`;
        }
      };

      // Set initial min-width
      updateMinWidth();

      // Update on resize (in case content changes)
      const resizeObserver = new ResizeObserver(updateMinWidth);
      resizeObserver.observe(buttonElement);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [isIconOnly, iconElement]);

  return (
    <button
      ref={buttonRef}
      type={type}
      onClick={onClick}
      disabled={isButtonDisabled}
      className={finalClassName}
      aria-label={replace(title)}
      title={replace(title)}
    >
      {loading && (
        <span className={`${sizeClasses.icon} animate-spin border-2 border-current border-t-transparent rounded-full w-4 h-4 inline-block ${showText ? 'mr-1.5' : ''}`}></span>
      )}
      {iconElement && !loading && iconElement}
      {showText && (
        <span className={iconElement && !loading ? `ml-1.5 ${sizeClasses.text}` : sizeClasses.text}>
          {replace(title)}
        </span>
      )}
    </button>
  );
};

ActionButton.propTypes = {
  action: PropTypes.oneOf([
    'add', 'edit', 'delete', 'save', 'search', 'reset', 'sync',
    'upload', 'download', 'clone', 'refresh', 'close', 'cancel', 'copy',
    'export', 'filter', 'remove', 'generate', 'convert', 'invalidate',
    'markRead', 'markUnread', 'buy', 'purchase', 'checkout', 'connect',
    'disconnect', 'resetPassword', 'view', 'manage', 'list', 'custom'
  ]).isRequired,
  icon: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.node
  ]),
  onClick: function (props, propName, componentName) {
    if (props.type === 'submit' && (props[propName] === undefined || props[propName] === null)) {
      return null; // onClick is optional for submit buttons
    }
    if (props.type !== 'submit' && typeof props[propName] !== 'function') {
      return new Error(`${propName} is required and must be a function for ${componentName} when type is not 'submit'`);
    }
    return null;
  },
  title: PropTypes.string.isRequired,
  showText: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'danger', 'success', 'warning']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};

export default ActionButton;

